import { ArrowRight, Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router";
import { MarketingFooter } from "~/app/components/landing/marketing-footer";
import { Button } from "~/app/components/ui/button";
import { getDocsNavigation } from "~/app/lib/docs.server";
import { cn } from "~/app/lib/utils";
import type { Route } from "./+types/docs";

export async function loader(_args: Route.LoaderArgs) {
  return getDocsNavigation();
}

export default function DocsLayout({ loaderData }: Route.ComponentProps) {
  const { groups } = loaderData;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const sidebar = (
    <nav className="space-y-1 px-5 py-8">
      <div className="space-y-5">
        {groups.map((group) => (
          <div key={group.group}>
            <h3 className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {group.group}
            </h3>
            <ul className="space-y-0.5">
              {group.pages.map((page) => (
                <li key={page.slug}>
                  <NavLink
                    to={`/docs/${page.slug}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "block rounded-md px-3 py-1.5 text-sm transition-colors",
                        isActive
                          ? "bg-[#023220]/5 font-medium text-[#023220]"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      )
                    }
                  >
                    {page.title}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="sticky top-0 z-50 border-b bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-5 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5 text-gray-600" />
              ) : (
                <Menu className="h-5 w-5 text-gray-600" />
              )}
            </button>
            <Link to="/docs" className="flex items-center gap-2">
              <img
                src="/assets/logo.svg"
                alt="AnyLawyer"
                className="w-[140px]"
              />
              <span className="border-l border-gray-200 pl-2 text-sm font-medium text-gray-600">
                Dokumentacja
              </span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="ghost"
              className="rounded-full text-slate-600 hover:text-slate-900"
            >
              <Link to="/">Strona główna</Link>
            </Button>
            <Button
              asChild
              className="rounded-full px-6 bg-brand-600 text-white hover:bg-brand-700"
            >
              <Link to="/app">
                Przejdź do AnyLawyer <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {mobileMenuOpen && <div className="border-b md:hidden">{sidebar}</div>}

      <div className="mx-auto flex w-full max-w-7xl flex-1">
        <aside className="hidden w-64 shrink-0 border-r md:block">
          <div className="sticky top-[69px] overflow-y-auto">{sidebar}</div>
        </aside>

        <main className="min-w-0 flex-1 px-6 py-8 sm:px-8 md:px-12 lg:px-16">
          <Outlet />
        </main>
      </div>

      <MarketingFooter />
    </div>
  );
}
